import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import axios from 'axios';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SIDES_TR = { north: 'Kuzey (A)', south: 'Güney (C)', east: 'Doğu (B)', west: 'Batı (D)' };

export default function ContractSignPage() {
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const token = params.get('token');

  const [state, setState] = useState('loading'); // loading | view | already_signed | success | error
  const [contract, setContract] = useState(null);
  const [signerName, setSignerName] = useState('');
  const [signing, setSigning] = useState(false);
  const [errorMsg, setErrorMsg] = useState('');
  const [agreed, setAgreed] = useState(false);
  const calledRef = useRef(false);

  useEffect(() => {
    if (!token) { setState('error'); setErrorMsg('Geçersiz bağlantı. Token bulunamadı.'); return; }
    if (calledRef.current) return;
    calledRef.current = true;

    axios.get(`${API}/customer/contracts/view`, { params: { token } })
      .then(r => {
        if (r.data.already_signed) {
          setContract(r.data);
          setState('already_signed');
        } else {
          setContract(r.data);
          setSignerName(r.data.full_name || '');
          setState('view');
        }
      })
      .catch(e => {
        setState('error');
        setErrorMsg(e.response?.data?.detail || 'Sözleşme yüklenemedi. Bağlantı süresi dolmuş olabilir.');
      });
  }, [token]);

  const handleSign = async () => {
    if (!signerName.trim() || signerName.trim().length < 3) {
      setErrorMsg('Lütfen tam adınızı girin (en az 3 karakter).');
      return;
    }
    if (!agreed) {
      setErrorMsg('Sözleşme koşullarını onaylamanız gerekmektedir.');
      return;
    }
    setSigning(true);
    setErrorMsg('');
    try {
      await axios.post(`${API}/customer/contracts/sign`, { signer_name: signerName }, { params: { token } });
      setState('success');
    } catch (e) {
      setErrorMsg(e.response?.data?.detail || 'İmzalama başarısız. Lütfen tekrar deneyin.');
    } finally {
      setSigning(false);
    }
  };

  const currSym = { USD: '$', EUR: '€', TRY: '₺' };
  const fmt = (n) => Number(n || 0).toLocaleString('tr-TR');

  if (state === 'loading') return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center">
      <div className="text-center">
        <div className="h-12 w-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin mx-auto mb-4" />
        <p className="text-gray-600 font-medium">Sözleşme yükleniyor...</p>
      </div>
    </div>
  );

  if (state === 'error') return (
    <div className="min-h-screen bg-gradient-to-br from-red-50 to-rose-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-xl p-10 max-w-md w-full text-center">
        <div className="text-6xl mb-4">⚠️</div>
        <h2 className="text-xl font-bold text-red-700 mb-2">Bağlantı Geçersiz</h2>
        <p className="text-gray-600 text-sm leading-relaxed">{errorMsg}</p>
        <p className="text-gray-400 text-xs mt-4">Sorun devam ederse organizasyon ekibiyle iletişime geçin.</p>
      </div>
    </div>
  );

  if (state === 'already_signed') return (
    <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-teal-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-xl p-10 max-w-md w-full text-center">
        <div className="text-6xl mb-4">✅</div>
        <h2 className="text-2xl font-bold text-emerald-700 mb-2">Sözleşme İmzalandı</h2>
        <p className="text-gray-600 text-sm mb-4">
          Bu sözleşme daha önce <strong>{contract?.contract_signer_name}</strong> tarafından dijital olarak imzalanmıştır.
        </p>
        {contract?.contract_signed_at && (
          <p className="text-xs text-gray-400">
            İmza tarihi: {new Date(contract.contract_signed_at).toLocaleString('tr-TR')}
          </p>
        )}
        <p className="text-xs text-gray-400 mt-6">COP 31 Blue Zone Platform · Antalya, Türkiye</p>
      </div>
    </div>
  );

  if (state === 'success') return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 to-indigo-100 flex items-center justify-center p-4">
      <div className="bg-white rounded-3xl shadow-xl p-10 max-w-md w-full text-center">
        <div className="text-6xl mb-6">🎉</div>
        <h2 className="text-2xl font-bold text-blue-800 mb-3">Sözleşme Başarıyla İmzalandı!</h2>
        <p className="text-gray-600 text-sm leading-relaxed mb-6">
          Stand alanı satış sözleşmeniz dijital olarak imzalandı. Organizasyon ekibimiz en kısa sürede sizinle iletişime geçecektir.
        </p>
        <div className="bg-blue-50 rounded-2xl p-4 mb-6 text-left">
          <p className="text-xs text-blue-500 font-semibold uppercase tracking-wide mb-1">İmzalayan</p>
          <p className="text-sm font-bold text-blue-800">{signerName}</p>
        </div>
        <button
          onClick={() => navigate('/giris')}
          className="w-full bg-blue-600 hover:bg-blue-700 text-white font-bold py-3 rounded-2xl transition-colors"
        >
          Paneline Giriş Yap →
        </button>
        <p className="text-xs text-gray-400 mt-4">COP 31 Blue Zone Platform · Antalya, Türkiye · 9–20 Kasım 2026</p>
      </div>
    </div>
  );

  // Main contract view
  const c = contract || {};
  const total = c.contract_total_amount || 0;
  const priceSqm = c.contract_price_per_sqm || 0;
  const currency = c.contract_currency || 'USD';
  const sym = currSym[currency] || currency;
  const openSides = (c.open_sides || []).map(s => SIDES_TR[s] || s).join(', ') || '—';

  return (
    <div className="min-h-screen bg-gradient-to-br from-blue-50 via-indigo-50 to-slate-100 py-8 px-4">
      <div className="max-w-2xl mx-auto">

        {/* Header */}
        <div className="bg-gradient-to-r from-blue-700 to-indigo-800 rounded-t-3xl p-8 text-center text-white shadow-xl">
          <div className="text-xs font-bold tracking-widest uppercase text-blue-200 mb-2">COP 31 Antalya · Blue Zone</div>
          <h1 className="text-2xl font-black mb-1">Stand Alanı Satış Sözleşmesi</h1>
          <p className="text-blue-200 text-sm">Digital Contract · Dijital Sözleşme</p>
        </div>

        <div className="bg-white rounded-b-3xl shadow-xl overflow-hidden">
          {/* Greeting */}
          <div className="px-8 pt-8 pb-4">
            <p className="text-gray-700 text-base">
              Sayın <strong>{c.full_name || c.company || 'Katılımcı'}</strong>,
            </p>
            <p className="text-gray-500 text-sm leading-relaxed mt-1">
              COP 31 Antalya Mavi Bölge stand alanı satış sözleşmeniz hazırlanmıştır. Lütfen aşağıdaki detayları inceleyerek dijital imzanızı atın.
            </p>
          </div>

          {/* Contract Details */}
          <div className="mx-8 mb-6 rounded-2xl overflow-hidden border border-blue-100">
            <div className="bg-blue-700 px-5 py-3">
              <span className="text-white text-xs font-bold tracking-widest uppercase">Sözleşme Detayları</span>
            </div>
            <table className="w-full text-sm">
              {[
                ['Firma / Kurum', c.company || c.country || '—'],
                ['Ülke', c.country || '—'],
                ['Stand Numarası', <strong className="text-blue-700 text-base font-bold">{c.stand_no || '—'}</strong>],
                ['Hall / Salon', c.hall || '—'],
                ['Stand Alanı', `${c.total_area || 0} m²`],
                ['Açık Kenarlar', openSides],
                ['m² Birim Fiyatı', `${sym}${fmt(priceSqm)} ${currency}`],
              ].map(([label, val], i) => (
                <tr key={i} className={i % 2 === 0 ? 'bg-blue-50/40' : 'bg-white'}>
                  <td className="px-5 py-3 text-gray-500 w-2/5">{label}</td>
                  <td className="px-5 py-3 font-semibold text-gray-800">{val}</td>
                </tr>
              ))}
              <tr className="bg-blue-50 border-t-2 border-blue-200">
                <td className="px-5 py-4 text-blue-800 font-black text-base">TOPLAM TUTAR</td>
                <td className="px-5 py-4 text-blue-700 font-black text-xl">{sym}{fmt(total)} {currency}</td>
              </tr>
            </table>
          </div>

          {/* Contract Terms */}
          <div className="mx-8 mb-6 bg-gray-50 rounded-2xl p-5 border border-gray-200">
            <h3 className="text-xs font-bold uppercase tracking-widest text-gray-500 mb-3">Sözleşme Koşulları</h3>
            <ol className="text-xs text-gray-600 leading-relaxed space-y-2 list-decimal pl-4">
              <li>İşbu sözleşme, yukarıda belirtilen stand alanının COP 31 Antalya etkinliği süresince (9–20 Kasım 2026) kullanım hakkını kapsamaktadır.</li>
              <li>Stand alanı bedeli sözleşme imzalandıktan sonra <strong>10 iş günü</strong> içinde ödenecektir. Ödeme gecikmeleri stand tahsisinin iptaline neden olabilir.</li>
              <li>Etkinlik başlangıcından 30+ gün önce yapılan iptallerde %100, 30–15 gün önce %50 iade yapılır. 15 günden kısa sürede iade yapılmaz.</li>
              <li>Stand alanı belirlenen sınırlar içinde kullanılacak; komşu standlara taşma yapılmayacaktır.</li>
              <li>Stand kurulumu etkinlik başlangıcından en az 2 gün önce tamamlanmalıdır.</li>
              <li>Organizatör, mücbir sebepler dışında stand alanını ve konumunu garanti eder.</li>
              <li>Bu sözleşme elektronik ortamda imzalanmakta olup yasal geçerlilik taşımaktadır.</li>
            </ol>
          </div>

          {/* Sign Section */}
          <div className="mx-8 mb-8">
            <div className="bg-gradient-to-br from-indigo-50 to-blue-50 rounded-2xl p-6 border-2 border-blue-200">
              <h3 className="text-sm font-bold text-blue-800 mb-4">Dijital İmza</h3>

              <div className="mb-4">
                <label className="block text-xs font-semibold text-gray-600 mb-1.5">Ad Soyad (İmzalayan)</label>
                <input
                  type="text"
                  value={signerName}
                  onChange={e => { setSignerName(e.target.value); setErrorMsg(''); }}
                  placeholder="Adınızı ve soyadınızı tam olarak girin"
                  className="w-full border border-blue-200 rounded-xl px-4 py-3 text-sm focus:outline-none focus:ring-2 focus:ring-blue-400 bg-white"
                  data-testid="signer-name-input"
                />
              </div>

              <label className="flex items-start gap-3 mb-5 cursor-pointer">
                <input
                  type="checkbox"
                  checked={agreed}
                  onChange={e => { setAgreed(e.target.checked); setErrorMsg(''); }}
                  className="mt-0.5 w-4 h-4 rounded accent-blue-600 flex-shrink-0"
                  data-testid="contract-agree-checkbox"
                />
                <span className="text-xs text-gray-600 leading-relaxed">
                  Yukarıdaki sözleşme detaylarını okudum, anladım ve koşulları kabul ediyorum. Bu dijital imzanın yasal geçerliliği olduğunu onaylıyorum.
                </span>
              </label>

              {errorMsg && (
                <div className="mb-4 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
                  {errorMsg}
                </div>
              )}

              <button
                onClick={handleSign}
                disabled={signing || !signerName.trim() || !agreed}
                className="w-full bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white font-black py-4 rounded-2xl text-base transition-all shadow-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
                data-testid="sign-contract-btn"
              >
                {signing ? (
                  <>
                    <div className="h-5 w-5 border-2 border-white border-t-transparent rounded-full animate-spin" />
                    İmzalanıyor...
                  </>
                ) : (
                  '✍ Sözleşmeyi Dijital Olarak İmzala'
                )}
              </button>
            </div>
          </div>

          <p className="text-center text-xs text-gray-400 pb-6">
            COP 31 Blue Zone Platform · Antalya, Türkiye · 9–20 Kasım 2026
          </p>
        </div>
      </div>
    </div>
  );
}
